import React, { forwardRef, useImperativeHandle, useEffect, useRef, useState, useMemo } from 'react';
import { View, StyleSheet, Platform } from 'react-native';
import { TopOpportunityItem } from '../TopOpportunitiesTable';

// Import logo
const warehouseNavigatorLogo = require('../../../../assets/warehouse_navigator.png');

// ============================================================================
// TYPES & INTERFACES
// ============================================================================

interface TopOpportunitiesBoardProps {
  activeTab?: 'board' | 'table';
  inProgressItems?: TopOpportunityItem[];
  completedItems?: TopOpportunityItem[];
  overdueItems?: TopOpportunityItem[];
  tableItems?: TopOpportunityItem[];
}

export interface TopOpportunitiesBoardPrintRef {
  print: () => void;
}

interface BoardPageData {
  inProgressPage: TopOpportunityItem[];
  donePage: TopOpportunityItem[];
  overduePage: TopOpportunityItem[];
}

interface ReportHeaderConfig {
  date: string;
  logoBase64: string;
}

interface BoardConfig {
  inProgressCount: number;
  doneCount: number;
  overdueCount: number;
}

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

/**
 * Format date to "DD MMM" format
 */
const formatDate = (dateString?: string): string => {
  if (!dateString) return '';
  try {
    const d = new Date(dateString);
    const day = d.getDate();
    const month = d.toLocaleString('en-US', { month: 'short' });
    return `${day} ${month}`;
  } catch {
    return '';
  }
};

/**
 * Escape HTML special characters for safe rendering
 * Works in both browser and non-browser environments
 */
const escapeHtml = (text: string): string => {
  const div = typeof document !== 'undefined' ? document.createElement('div') : null;
  if (div) {
    div.textContent = text;
    return div.innerHTML;
  }
  // Fallback for non-browser environments
  return text.replace(/[&<>"']/g, (m) => ({
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#39;',
  }[m] || m));
};

/**
 * Split array into chunks of specified size
 */
const chunkBy = <T,>(items: T[], chunkSize: number): T[][] => {
  if (chunkSize <= 0) return [items];
  if (items.length === 0) return [[]];
  const out: T[][] = [];
  for (let i = 0; i < items.length; i += chunkSize) {
    out.push(items.slice(i, i + chunkSize));
  }
  return out;
};

// ============================================================================
// CSS STYLESHEET GENERATOR
// ============================================================================

/**
 * Generate complete CSS stylesheet for print rendering
 */
const generatePrintStyles = (): string => {
  return `
    @page {
      size: landscape;
      margin: 0.5in 0.25in;
    }

    * {
      margin: 0;
      padding: 0;
      box-sizing: border-box;
    }

    html, body {
      height: 100%;
      width: 100%;
    }

    body {
      font-family: Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
      font-size: 9px;
      background: white;
      color: #1C1C1C;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }

    :root {
      --page-header-height: 130px;
    }

    .page {
      width: 100%;
    }

    .page-body {
      width: 100%;
    }

    .report-header {
      background: white;
      margin-bottom: 12px;
    }

    .header-content {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 8px 12px;
      border: 1px solid #DADADA;
      border-radius: 6px;
      margin-bottom: 12px;
      background: white;
    }

    .header-title-container {
      display: flex;
      align-items: center;
      gap: 8px;
    }

    .header-logo {
      width: 20px;
      height: 20px;
      border-radius: 4px;
      background-size: contain;
      background-position: center;
      background-repeat: no-repeat;
      flex-shrink: 0;
    }

    .header-title {
      font-size: 16px;
      font-weight: 700;
      color: #093F6E;
      line-height: 1;
    }

    .header-date {
      font-size: 13px;
      font-weight: 400;
      color: #6B6B6B;
    }

    .section-title {
      font-size: 16px;
      font-weight: 700;
      color: #4B4B4B;
      margin: 12px 0 12px 10px;
    }

    .column-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 8px;
      padding: 8px 16px;
      background: #F8F9FA;
      border: 1px solid #E2E8F0;
      border-radius: 8px;
    }

    .column-header-title {
      font-size: 12px;
      font-weight: 700;
      color: #4B4B4B;
    }

    .column-header-badge {
      background: #106E98;
      color: white;
      font-size: 12px;
      font-weight: 700;
      padding: 2px 8px;
      border-radius: 10px;
      min-width: 24px;
      text-align: center;
    }

    .column-table {
      width: 100%;
      border-collapse: separate;
      border-spacing: 0;
    }

    .column-table th {
      padding: 0;
      text-align: left;
      vertical-align: top;
      padding-bottom: 10px;
    }

    .column-table td {
      padding: 0;
      vertical-align: top;
    }

    .column-card-cell {
      padding: 0;
    }

    .card-gap {
      display: block;
      padding-bottom: 8px;
    }

    .column-table tbody tr:last-child .card-gap {
      padding-bottom: 0;
    }

    .board-table {
      width: 100%;
      table-layout: fixed;
      border-collapse: separate;
      border-spacing: 12px;
      margin-bottom: 16px;
    }

    .board-table td {
      padding: 0;
      vertical-align: top;
    }

    .board-column-cell {
      border: 1px solid #E2E8F0;
      border-radius: 8px;
      padding: 12px 8px;
      background: white;
      -webkit-box-decoration-break: clone;
      box-decoration-break: clone;
    }

    .board-card {
      background: white;
      border: 1px solid #E2E8F0;
      border-radius: 8px;
      padding: 10px;
      margin-bottom: 0;
      page-break-inside: avoid;
      break-inside: avoid;
    }

    .card-title-row {
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin-bottom: 6px;
      gap: 8px;
    }

    .card-title {
      font-size: 12px;
      font-weight: 500;
      color: #1C1C1C;
      flex: 1;
    }

    .overdue-badge {
      background: #C12C01;
      color: white;
      font-size: 10px;
      font-weight: 700;
      padding: 4px 8px;
      border-radius: 12px;
      white-space: nowrap;
    }

    .card-description {
      font-size: 9px;
      font-weight: 400;
      color: #4B4B4B;
      line-height: 12px;
      margin-bottom: 6px;
      display: -webkit-box;
      -webkit-line-clamp: 3;
      -webkit-box-orient: vertical;
      overflow: hidden;
    }

    .card-footer {
      display: flex;
      justify-content: space-between;
      align-items: center;
      font-size: 9px;
      color: #4B4B4B;
    }

    @media print {
      @page {
        size: landscape;
        margin: 0.5in 0.25in;
      }

      * {
        -webkit-print-color-adjust: exact !important;
        print-color-adjust: exact !important;
        color-adjust: exact !important;
      }

      html, body {
        margin: 0 !important;
        padding: 0 !important;
        width: 100% !important;
        height: auto !important;
      }

      .page {
        page-break-after: always;
        break-after: page;
        min-height: 100vh;
        overflow: hidden;
      }

      .page--last {
        page-break-after: auto;
        break-after: auto;
      }

      .report-header {
        page-break-after: avoid;
        break-after: avoid;
        page-break-inside: avoid;
        break-inside: avoid;
      }

      .page-body {
        height: calc(100vh - var(--page-header-height));
      }

      .board-table {
        height: 100%;
      }

      .board-table tr,
      .board-table td {
        height: 100%;
      }

      .board-column-cell {
        height: 100%;
      }

      .board-table {
        border-collapse: separate;
        border-spacing: 12px;
      }

      .board-column-cell {
        page-break-inside: auto;
        break-inside: auto;
      }

      .board-card {
        page-break-inside: avoid;
        break-inside: avoid;
        orphans: 3;
        widows: 3;
      }
    }
  `;
};

// ============================================================================
// HTML RENDERING FUNCTIONS
// ============================================================================

/**
 * Render a single opportunity card
 */
const renderCard = (item: TopOpportunityItem): string => {
  const isOverdue = item.STATUS && String(item.STATUS).toLowerCase() === 'overdue';
  return `
    <div class="board-card">
      <div class="card-title-row">
        <div class="card-title">${escapeHtml(item.TITLE || '')}</div>
        ${isOverdue ? '<div class="overdue-badge">OVERDUE</div>' : ''}
      </div>
      <div class="card-description">${escapeHtml(item.ACTION_PLAN_DESCRIPTION || '')}</div>
      <div class="card-footer">
        <span class="card-type">${escapeHtml(item.TYPE || '')}</span>
        <span class="card-date">${formatDate(item.TARGET_DATE)}</span>
      </div>
    </div>
  `;
};

/**
 * Render a column table with header and cards
 */
const renderColumnTable = (opts: {
  title: string;
  count: number;
  items: TopOpportunityItem[];
}): string => {
  const cardRows = opts.items
    .map(
      (item) =>
        `<tr><td class="column-card-cell"><div class="card-gap">${renderCard(item)}</div></td></tr>`,
    )
    .join('');

  const emptyRow =
    opts.items.length === 0
      ? '<tr><td class="column-card-cell"><div style="padding: 20px; text-align: center; color: #999; font-size: 12px;"></div></td></tr>'
      : '';

  return `
    <table class="column-table">
      <thead>
        <tr>
          <th>
            <div class="column-header">
              <span class="column-header-title">${escapeHtml(opts.title)}</span>
              <span class="column-header-badge">${opts.count}</span>
            </div>
          </th>
        </tr>
      </thead>
      <tbody>
        ${cardRows}${emptyRow}
      </tbody>
    </table>
  `;
};

/**
 * Render report header with logo and title
 */
const renderReportHeader = (config: ReportHeaderConfig): string => {
  return `
    <div class="report-header">
      <div class="header-content">
        <div class="header-title-container">
          <div class="header-logo" style="background-image: url('${config.logoBase64}');"></div>
          <span class="header-title">Warehouse Navigator</span>
        </div>
        <span class="header-date">${config.date}</span>
      </div>
      <div class="section-title">Top Opportunities</div>
    </div>
  `;
};

/**
 * Render board with three columns for a given page
 */
const renderBoard = (pageData: BoardPageData, boardConfig: BoardConfig): string => {
  return `
    <div class="page-body">
      <table class="board-table">
        <tr>
          <td>
            <div class="board-column-cell">
              ${renderColumnTable({ title: 'In Progress', count: boardConfig.inProgressCount, items: pageData.inProgressPage })}
            </div>
          </td>
          <td>
            <div class="board-column-cell">
              ${renderColumnTable({ title: 'Done', count: boardConfig.doneCount, items: pageData.donePage })}
            </div>
          </td>
          <td>
            <div class="board-column-cell">
              ${renderColumnTable({ title: 'Overdue', count: boardConfig.overdueCount, items: pageData.overduePage })}
            </div>
          </td>
        </tr>
      </table>
    </div>
  `;
};

// ============================================================================
// PAGINATION LOGIC
// ============================================================================

/**
 * Generate paginated board data from item lists
 */
const generatePagedData = (
  inProgressItems: TopOpportunityItem[],
  doneItems: TopOpportunityItem[],
  overdueItems: TopOpportunityItem[],
  cardsPerPage: number = 6,
): BoardPageData[] => {
  const inChunks = chunkBy(inProgressItems, cardsPerPage);
  const doneChunks = chunkBy(doneItems, cardsPerPage);
  const overdueChunks = chunkBy(overdueItems, cardsPerPage);
  const pageCount = Math.max(inChunks.length, doneChunks.length, overdueChunks.length);

  return Array.from({ length: pageCount }, (_, idx) => ({
    inProgressPage: inChunks[idx] ?? [],
    donePage: doneChunks[idx] ?? [],
    overduePage: overdueChunks[idx] ?? [],
  }));
};

// ============================================================================
// HTML DOCUMENT GENERATOR
// ============================================================================
/**
 * Generate complete HTML document for warehouse report printing
 * Handles pagination, headers, and responsive layout across browsers
 */
const generateWarehouseReportHTML = ({
  date,
  inProgressCount,
  doneCount,
  overdueCount,
  inProgressItems,
  doneItems,
  overdueItems,
  logoBase64 = '',
}: {
  date: string;
  inProgressCount: number;
  doneCount: number;
  overdueCount: number;
  inProgressItems: TopOpportunityItem[];
  doneItems: TopOpportunityItem[];
  overdueItems: TopOpportunityItem[];
  logoBase64?: string;
}): string => {
  // Generate paginated data for all pages
  const pagedData = generatePagedData(
    inProgressItems,
    doneItems,
    overdueItems,
    6, // cardsPerPage: adjusted to account for 8px inter-card spacing
  );

  // Render header config
  const headerConfig: ReportHeaderConfig = {
    date,
    logoBase64,
  };

  // Render board config
  const boardConfig: BoardConfig = {
    inProgressCount,
    doneCount,
    overdueCount,
  };

  // Build HTML for all pages
  const pagesHtml = pagedData
    .map((pageData, idx) => {
      const isLastPage = idx === pagedData.length - 1;
      const pageClass = isLastPage ? 'page page--last' : 'page';
      return `
        <section class="${pageClass}">
          ${renderReportHeader(headerConfig)}
          ${renderBoard(pageData, boardConfig)}
        </section>
      `;
    })
    .join('');

  // Assemble final HTML document
  return `<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <style>
    ${generatePrintStyles()}
  </style>
</head>
<body>
  ${pagesHtml}
</body>
</html>`;
};

// ============================================================================
// REACT COMPONENT
// ============================================================================

const TopOpportunitiesBoard = forwardRef<
  TopOpportunitiesBoardPrintRef,
  TopOpportunitiesBoardProps
>(
  (
    {
      activeTab = 'board',
      inProgressItems = [],
      completedItems = [],
      overdueItems = [],
      tableItems = [],
    },
    ref
  ) => {
    const iframeRef = useRef<HTMLIFrameElement | null>(null);
    const [logoBase64, setLogoBase64] = useState<string>('');

    // Load logo on mount
    useEffect(() => {
      const loadLogo = async () => {
        if (Platform.OS === 'web') {
          try {
            // For web, fetch and convert to base64
            const response = await fetch(warehouseNavigatorLogo);
            const blob = await response.blob();
            const reader = new FileReader();
            reader.onloadend = () => {
              setLogoBase64(reader.result as string);
            };
            reader.readAsDataURL(blob);
          } catch (error) {
            console.warn('Failed to load logo:', error);
            setLogoBase64('');
          }
        }
      };
      loadLogo();
    }, []);

    // Generate current date
    const currentDate = new Date().toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });

    // Memoize HTML content generation to avoid unnecessary recomputation
    const htmlContent = useMemo(
      () =>
        generateWarehouseReportHTML({
          date: currentDate,
          inProgressCount: inProgressItems.length,
          doneCount: completedItems.length,
          overdueCount: overdueItems.length,
          inProgressItems,
          doneItems: completedItems,
          overdueItems,
          logoBase64,
        }),
      [
        currentDate,
        inProgressItems,
        completedItems,
        overdueItems,
        logoBase64,
      ]
    );

    // Export print functionality via imperative handle
    useImperativeHandle(ref, () => ({
      print: () => {
        if (Platform.OS === 'web') {
          const iframe = iframeRef.current;
          if (iframe?.contentWindow) {
            iframe.contentWindow.print();
          }
        } else {
          // For mobile, use react-native-print or react-native-html-to-pdf
          // Example: RNPrint.print({ html: htmlContent });
          console.log('Mobile print - use react-native-print with HTML:', htmlContent);
        }
      },
    }), [htmlContent]);

    // Update iframe content when HTML changes
    useEffect(() => {
      if (Platform.OS === 'web' && iframeRef.current?.contentDocument) {
        const doc = iframeRef.current.contentDocument;
        doc.open();
        doc.write(htmlContent);
        doc.close();
      }
    }, [htmlContent]);

    if (Platform.OS !== 'web') {
      // For mobile, return empty view - printing is handled via HTML string
      return <View style={styles.hiddenContainer} />;
    }

    return (
      <View style={styles.hiddenContainer}>
        {/* @ts-ignore - iframe is web-only */}
        <iframe
          ref={iframeRef}
          style={{
            position: 'absolute',
            width: '1px',
            height: '1px',
            opacity: 0,
            pointerEvents: 'none',
          }}
          title="Print Preview"
        />
      </View>
    );
  }
);

TopOpportunitiesBoard.displayName = 'TopOpportunitiesBoard';

const styles = StyleSheet.create({
  hiddenContainer: {
    position: 'absolute',
    left: -9999,
    top: -9999,
    width: 1,
    height: 1,
    opacity: 0,
  },
});

export default TopOpportunitiesBoard;
